import type { Reference } from '@medplum/fhirtypes'
import type { JsonValue } from '#src/json.ts'
import { VALIDATED_TOOLS } from '#src/models/questionnaires/definitions/validated-tools/validatedTools.ts'
import { extractId, parseReferenceString } from '#src/utils.ts'

/* A validated tool (Braden, PHQ-9, …) placed in a note or assessment through
 * the rich-text editor stores its answers in a QuestionnaireResponse and keeps
 * the reference to that response inside the editor node's `response`
 * attribute. Nothing is written back onto the response, so the section that
 * embeds it is the only place the link exists, and the parent composition can
 * only be found by searching for the section that names the response.
 *
 * `Composition.section.entry` is that searchable link. It is recomputed from
 * the stored content on every save rather than tracked incrementally, so
 * deleting a tool from the editor drops its entry in the same write.
 *
 * A regex rather than a DOM walk because core must stay free of a DOM, and
 * because the stored content is not always HTML: after the native-note JSON
 * migration a section body may be a JSON.stringify'd TipTap document, where the
 * same value sits under a `"response"` key. Both spellings are matched here so
 * one implementation covers every stored format. */
const RESPONSE_ATTRIBUTE =
  /(?:data-response|"response")\s*[:=]\s*["']([^"']*)["']/g

/* Every validated-tool response the stored content embeds, as canonical
 * `QuestionnaireResponse/<id>` reference strings, deduplicated and in the order
 * they appear. A versioned or otherwise decorated reference normalizes onto the
 * same key the read-side parent lookup builds, so the two cannot drift. */
export function validatedToolResponseReferences(
  content: string | undefined | null
): string[] {
  if (!content) {
    return []
  }
  const references = new Set<string>()
  for (const [, value] of content.matchAll(RESPONSE_ATTRIBUTE)) {
    /* A node whose form has not been submitted yet carries no response, and a
     * reference naming some other resource type is not this link — neither is
     * a parent. */
    if (!value) {
      continue
    }
    const type = parseReferenceString(value)?.type
    if (type && type !== 'QuestionnaireResponse') {
      continue
    }
    const id = extractId(value)
    if (id) {
      references.add(`QuestionnaireResponse/${id}`)
    }
  }
  return [...references]
}

/* One validated-tool node in stored HTML, and the name attribute inside it.
 * Scoped to the element so attribute order does not matter: `mergeAttributes`
 * does not guarantee `data-type` comes before `data-name`. */
const VALIDATED_TOOL_TAG =
  /<div[^>]*\bdata-type\s*=\s*["']validated-tool["'][^>]*>/g
const NAME_ATTRIBUTE = /\bdata-name\s*=\s*["']([^"']*)["']/
const RESPONSE_VALUE_ATTRIBUTE = /\bdata-response\s*=\s*["']([^"']*)["']/

/* Every unfilled validated tool the stored content embeds, by node name
 * (`braden`, `gad-7`, …), canonical and deduplicated in document order.
 *
 * A staged proposal only renders on a node with no response. Filled nodes are
 * omitted so the gate names exactly the nodes that can display a proposal.
 *
 * Both stored formats are handled, but not with one pattern: a `"name"` key is
 * far too common in a TipTap document to match on its own, so JSON is parsed
 * and walked for nodes actually typed `validated-tool`, and only the HTML form
 * falls back to a regex (still element-scoped). Content that starts with `{`
 * but does not parse is treated as HTML rather than thrown away. */
export function validatedToolNames(
  content: string | undefined | null
): string[] {
  if (!content) {
    return []
  }
  const names = new Set<string>()
  const trimmed = content.trim()
  if (trimmed.startsWith('{')) {
    try {
      const parsed: JsonValue = JSON.parse(trimmed)
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        Array.isArray(parsed) ||
        (parsed as { type?: unknown }).type !== 'doc'
      ) {
        return []
      }
      collectValidatedToolNames(parsed, names)
      return [...names]
    } catch {
      // Not a TipTap document after all; fall through to the HTML form.
    }
  }
  for (const [tag] of trimmed.matchAll(VALIDATED_TOOL_TAG)) {
    if (!tag.match(RESPONSE_VALUE_ATTRIBUTE)?.[1]) {
      addValidatedToolName(tag.match(NAME_ATTRIBUTE)?.[1], names)
    }
  }
  return [...names]
}

function addValidatedToolName(
  value: JsonValue | undefined,
  into: Set<string>
): void {
  if (typeof value !== 'string') {
    return
  }
  if (
    value &&
    value === value.trim().toLowerCase() &&
    Object.hasOwn(VALIDATED_TOOLS, value)
  ) {
    into.add(value)
  }
}

function collectValidatedToolNames(
  node: JsonValue | undefined,
  into: Set<string>
): void {
  if (Array.isArray(node)) {
    for (const child of node) {
      collectValidatedToolNames(child, into)
    }
    return
  }
  if (!node || typeof node !== 'object') {
    return
  }
  const { type, attrs, content } = node as {
    type?: JsonValue
    attrs?: JsonValue
    content?: JsonValue
  }
  if (type === 'validated-tool') {
    const { name, response } =
      (attrs as { name?: JsonValue; response?: JsonValue } | undefined) ?? {}
    if (!response) {
      addValidatedToolName(name, into)
    }
  }
  collectValidatedToolNames(content, into)
}

/* The `section.entry` a section should carry once its content has been saved:
 * exactly the validated-tool responses the content embeds, plus whatever else
 * the section already links to.
 *
 * Entries naming other resource types are kept because `section.entry` is not
 * this feature's alone — signing a visit note files its clinical mentions
 * (conditions, observations, medications, documents) there, and a content save
 * must not drop them. Every QuestionnaireResponse entry, by contrast, is
 * replaced: the content is the record of which tools are embedded, so a tool
 * removed from the editor loses its entry here. */
export function withValidatedToolEntries(
  existing: Reference[] | undefined,
  content: string | undefined | null
): Reference[] {
  const others = (existing ?? []).filter(
    (entry) =>
      parseReferenceString(entry.reference)?.type !== 'QuestionnaireResponse'
  )
  return [
    ...others,
    ...validatedToolResponseReferences(content).map((reference) => ({
      reference,
    })),
  ]
}
